import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Upload, Loader2, Shield, AlertTriangle, 
  CheckCircle, XCircle, ChevronDown, ChevronUp,
  MessageSquare, Target, AlertCircle, FileSearch,
  DollarSign, Scale, BookOpen, Eye, EyeOff, Maximize2, Minimize2, X,
  MapPin, Building2, Gavel, Download
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PolicyAnalysisTab = ({ claimId, userId, claim }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [hasPolicyDocument, setHasPolicyDocument] = useState(false);
  const [policyUrl, setPolicyUrl] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isPdfFullscreen, setIsPdfFullscreen] = useState(false);
  const [zoningData, setZoningData] = useState(null);
  const [isLoadingZoning, setIsLoadingZoning] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    coverage: true,
    exclusions: true,
    strategy: true,
    talkingPoints: false,
    citations: false,
    zoning: true
  });

  useEffect(() => {
    loadAnalysis();
  }, [claimId]);

  // Load zoning data when claim address is available
  useEffect(() => {
    // Get address from claim property_address OR from linked estimate
    const address = claim?.property_address || claim?.estimate?.address;
    if (address) {
      loadZoningData(address);
    }
  }, [claim?.property_address, claim?.estimate?.address]);

  const loadZoningData = async (address) => {
    try {
      setIsLoadingZoning(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/zoning/lookup`,
        { params: { address } }
      );
      
      if (response.data.success) {
        setZoningData(response.data);
      }
    } catch (error) {
      console.error('Error loading zoning data:', error);
      // Don't show error toast - zoning is supplementary info
    } finally {
      setIsLoadingZoning(false);
    }
  };

  const loadAnalysis = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/claims/${claimId}/policy-analysis?user_id=${userId}`
      );
      
      if (response.data.success) {
        setAnalysis(response.data.analysis);
      }
      setHasPolicyDocument(response.data.has_policy || false);
      setPolicyUrl(response.data.policy_url || null);
    } catch (error) {
      console.error('Error loading policy analysis:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedExts = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExts.includes(fileExt)) {
      toast.error('Please upload a PDF or image file (JPG, PNG, WEBP)');
      return;
    }

    if (file.size > 20 * 1024 * 1024) { // 20MB limit
      toast.error('File too large. Maximum size is 20MB');
      return;
    }

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/claims/${claimId}/upload-policy?user_id=${userId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.success) {
        setHasPolicyDocument(true);
        setPolicyUrl(response.data.policy_url);
        setUploadedFileName(file.name);
        
        if (response.data.text_extracted) {
          toast.success(`Policy uploaded! Text extracted (${response.data.text_length} chars). Click "Analyze Policy" to run AI analysis.`);
        } else {
          toast.success('Policy uploaded! Click "Analyze Policy" to extract and analyze.');
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload policy');
    } finally {
      setIsUploading(false);
    }
  };

  const analyzePolicy = async () => {
    setIsAnalyzing(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/claims/${claimId}/analyze-policy?user_id=${userId}`
      );

      if (response.data.success) {
        setAnalysis(response.data.analysis);
        toast.success('Policy analysis complete!');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze policy');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-100';
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const downloadAnalysis = () => {
    if (!analysis) return;
    let text = `POLICY ANALYSIS REPORT\n${'='.repeat(50)}\n`;
    text += `Claim: ${claim?.claim_number || claimId}\n`;
    text += `Date: ${new Date().toLocaleDateString()}\n\n`;
    if (analysis.policy_summary) text += `POLICY SUMMARY\n${'-'.repeat(30)}\n${analysis.policy_summary}\n\n`;
    if (analysis.coverage_assessment) text += `COVERAGE ASSESSMENT\n${'-'.repeat(30)}\n${analysis.coverage_assessment}\n\n`;
    if (analysis.key_provisions?.length > 0) {
      text += `KEY PROVISIONS\n${'-'.repeat(30)}\n`;
      analysis.key_provisions.forEach(p => text += `- ${p}\n`);
      text += '\n';
    }
    if (analysis.exclusions?.length > 0) {
      text += `EXCLUSIONS\n${'-'.repeat(30)}\n`;
      analysis.exclusions.forEach(e => text += `- ${e}\n`);
      text += '\n';
    }
    if (analysis.recommendations?.length > 0) {
      text += `RECOMMENDATIONS\n${'-'.repeat(30)}\n`;
      analysis.recommendations.forEach(r => text += `- ${r}\n`);
      text += '\n';
    }
    if (analysis.risk_factors?.length > 0) {
      text += `RISK FACTORS\n${'-'.repeat(30)}\n`;
      analysis.risk_factors.forEach(r => text += `[${(r.level || '').toUpperCase()}] ${r.factor}: ${r.description || ''}\n`);
    }
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `policy-analysis-${claim?.claim_number || claimId}.txt`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Policy analysis downloaded');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // PDF/Image Preview Component
  const PolicyPreviewPanel = () => {
    if (!policyUrl) return null;
    
    const isImage = policyUrl.match(/\.(jpg|jpeg|png|webp|heic)$/i) || 
                    (policyUrl.includes('/lead-files/') && !policyUrl.endsWith('.pdf'));

    if (isPdfFullscreen) {
      return (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
          <div className="flex items-center justify-between p-4 bg-slate-900">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Insurance Policy Document
            </h3>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(policyUrl, '_blank')}
                className="text-white hover:bg-slate-700"
              >
                Open in New Tab
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsPdfFullscreen(false)}
                className="text-white hover:bg-slate-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <div className="flex-1 p-4 overflow-auto flex items-center justify-center">
            {isImage ? (
              <img src={policyUrl} alt="Policy Document" className="max-w-full max-h-full object-contain" />
            ) : (
              <iframe
                src={`${policyUrl}#toolbar=1&navpanes=1`}
                className="w-full h-full rounded-lg"
                title="Policy Document"
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base text-blue-900">
              <FileText className="w-5 h-5" />
              Policy Document {isImage ? '(Image)' : '(PDF)'}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPdfPreview(!showPdfPreview)}
                className="text-blue-700 hover:bg-blue-100"
              >
                {showPdfPreview ? (
                  <>
                    <EyeOff className="w-4 h-4 mr-1" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-1" />
                    Show
                  </>
                )}
              </Button>
              {showPdfPreview && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsPdfFullscreen(true)}
                  className="text-blue-700 hover:bg-blue-100"
                >
                  <Maximize2 className="w-4 h-4 mr-1" />
                  Fullscreen
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(policyUrl, '_blank')}
                className="text-blue-700 hover:bg-blue-100"
              >
                Open in New Tab
              </Button>
            </div>
          </div>
        </CardHeader>
        {showPdfPreview && (
          <CardContent className="pt-0">
            <div className="bg-white rounded-lg border border-blue-200 overflow-hidden" style={{ height: isImage ? 'auto' : '500px' }}>
              {isImage ? (
                <img src={policyUrl} alt="Policy Document" className="w-full object-contain max-h-[600px]" />
              ) : (
                <iframe
                  src={`${policyUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                  className="w-full h-full"
                  title="Policy Document Preview"
                />
              )}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  // Zoning Requirements Component
  const ZoningRequirementsPanel = () => {
    if (isLoadingZoning) {
      return (
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="py-6">
            <div className="flex items-center justify-center gap-2 text-purple-700">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading county zoning requirements...
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!zoningData) return null;

    const { county, general_requirements, county_specific, defense_strategies } = zoningData;

    return (
      <Card className="border-purple-200 bg-purple-50">
        <CardHeader className="py-3">
          <button
            onClick={() => toggleSection('zoning')}
            className="w-full flex items-center justify-between"
          >
            <CardTitle className="flex items-center gap-2 text-base text-purple-900">
              <Gavel className="w-5 h-5" />
              County Zoning & Code Requirements
            </CardTitle>
            {expandedSections.zoning ? (
              <ChevronUp className="w-5 h-5 text-purple-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-purple-600" />
            )}
          </button>
        </CardHeader>
        
        {expandedSections.zoning && (
          <CardContent className="pt-0 space-y-4">
            {/* County Info */}
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-purple-600" />
                <span className="font-semibold text-purple-900">
                  {county?.name} County, {county?.state_abbr}
                </span>
                <Badge variant="outline" className="text-xs">
                  FIPS: {county?.fips}
                </Badge>
              </div>
              <p className="text-sm text-purple-700">
                Requirements identified from local building codes can be used to defend against claim denials.
              </p>
            </div>

            {/* General Requirements */}
            {general_requirements && Object.keys(general_requirements).length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  State Building Requirements
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {general_requirements.wind_zone && (
                    <div className="text-sm">
                      <span className="font-medium text-purple-800">Wind Zone:</span>
                      <p className="text-purple-700">{general_requirements.wind_zone}</p>
                    </div>
                  )}
                  {general_requirements.ice_dam_protection && (
                    <div className="text-sm">
                      <span className="font-medium text-purple-800">Ice Dam Protection:</span>
                      <p className="text-purple-700">{general_requirements.ice_dam_protection}</p>
                    </div>
                  )}
                  {general_requirements.underlayment && (
                    <div className="text-sm">
                      <span className="font-medium text-purple-800">Underlayment:</span>
                      <p className="text-purple-700">{general_requirements.underlayment}</p>
                    </div>
                  )}
                  {general_requirements.fastening && (
                    <div className="text-sm">
                      <span className="font-medium text-purple-800">Fastening:</span>
                      <p className="text-purple-700">{general_requirements.fastening}</p>
                    </div>
                  )}
                  {general_requirements.licensed_contractor && (
                    <div className="text-sm">
                      <span className="font-medium text-purple-800">Contractor License:</span>
                      <p className="text-purple-700">{general_requirements.licensed_contractor}</p>
                    </div>
                  )}
                  {general_requirements.permit_required && (
                    <div className="text-sm">
                      <span className="font-medium text-purple-800">Permit Required:</span>
                      <p className="text-purple-700">{general_requirements.permit_required === true ? 'Yes' : general_requirements.permit_required}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* County-Specific Requirements */}
            {county_specific && Object.keys(county_specific).length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-3">
                  {county?.name} County Specific
                </h4>
                <div className="space-y-2">
                  {county_specific.permit_fee && (
                    <div className="text-sm">
                      <span className="font-medium text-purple-800">Permit Fees:</span>
                      <span className="text-purple-700 ml-2">{county_specific.permit_fee}</span>
                    </div>
                  )}
                  {county_specific.inspection_types && (
                    <div className="text-sm">
                      <span className="font-medium text-purple-800">Inspections Required:</span>
                      <span className="text-purple-700 ml-2">{county_specific.inspection_types.join(', ')}</span>
                    </div>
                  )}
                  {county_specific.special_requirements && (
                    <div className="text-sm">
                      <span className="font-medium text-purple-800">Special Requirements:</span>
                      <span className="text-purple-700 ml-2">{county_specific.special_requirements}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Defense Strategies */}
            {defense_strategies && defense_strategies.length > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Claim Defense Points Based on Local Codes
                </h4>
                <div className="space-y-3">
                  {defense_strategies.map((strategy, idx) => (
                    <div key={idx} className="text-sm">
                      <p className="font-medium text-green-800 mb-1">{strategy.description}</p>
                      <ul className="list-disc list-inside text-green-700 space-y-1 ml-2">
                        {strategy.points.slice(0, 3).map((point, pidx) => (
                          <li key={pidx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // No analysis yet - show upload UI
  if (!analysis) {
    return (
      <div className="space-y-6">
        {/* Upload Section */}
        <Card className="border-2 border-dashed border-slate-300 bg-slate-50">
          <CardContent className="py-12">
            <div className="text-center">
              <FileSearch className="w-16 h-16 mx-auto text-slate-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                {hasPolicyDocument ? 'Policy Uploaded' : 'Upload Insurance Policy'}
              </h3>
              
              {/* Show uploaded file status */}
              {hasPolicyDocument && policyUrl && (
                <div className="my-4 mx-auto max-w-md bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-green-800">File Uploaded Successfully</p>
                      {uploadedFileName && (
                        <p className="text-xs text-green-600 truncate max-w-[250px]">{uploadedFileName}</p>
                      )}
                    </div>
                    <a href={policyUrl} target="_blank" rel="noreferrer" className="text-green-700 hover:text-green-900 ml-2">
                      <Eye className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              )}

              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                {hasPolicyDocument 
                  ? 'Your policy is ready. Click "Analyze Policy" to run the AI analysis, or upload a different file.'
                  : 'Upload the homeowner\'s insurance policy (PDF or image) to get AI-powered analysis and claim defense strategy.'
                }
              </p>

              <div className="flex flex-col items-center gap-4">
                {/* Upload button */}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={isUploading}
                    data-testid="policy-file-input"
                  />
                  <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                    hasPolicyDocument 
                      ? 'bg-slate-200 text-slate-700 hover:bg-slate-300' 
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}>
                    {isUploading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        {hasPolicyDocument ? 'Upload Different File' : 'Select Policy File (PDF or Image)'}
                      </>
                    )}
                  </div>
                </label>
                <p className="text-xs text-slate-400">PDF, JPG, PNG accepted — max 20MB</p>

                {/* Analyze button — prominent when file is uploaded */}
                {hasPolicyDocument && (
                  <Button 
                    onClick={analyzePolicy} 
                    disabled={isAnalyzing}
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 text-lg px-8 py-4 mt-2"
                    data-testid="analyze-policy-btn"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Analyzing Policy...
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5 mr-2" />
                        Analyze Policy
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What You'll Get */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <Shield className="w-8 h-8 text-blue-500 mb-3" />
              <h4 className="font-semibold text-slate-900 mb-1">Coverage Analysis</h4>
              <p className="text-sm text-slate-600">
                Identify coverage limits, deductibles, and what's covered for this claim.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <AlertTriangle className="w-8 h-8 text-amber-500 mb-3" />
              <h4 className="font-semibold text-slate-900 mb-1">Exclusion Alerts</h4>
              <p className="text-sm text-slate-600">
                Spot potential exclusions and limitations that could affect the claim.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Target className="w-8 h-8 text-green-500 mb-3" />
              <h4 className="font-semibold text-slate-900 mb-1">Defense Strategy</h4>
              <p className="text-sm text-slate-600">
                Get talking points and arguments for the adjuster meeting.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Zoning Requirements - Show even without policy upload */}
        <ZoningRequirementsPanel />
      </div>
    );
  }

  // Show analysis results
  return (
    <div className="space-y-6">
      {/* PDF Preview Panel - Show at top when policy exists */}
      {policyUrl && <PolicyPreviewPanel />}

      {/* Zoning Requirements Panel - Always show when claim has address */}
      <ZoningRequirementsPanel />

      {/* Header with Confidence Score */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Policy Analysis</h3>
          <p className="text-sm text-slate-500">
            {analysis.insurance_company || 'Insurance Company'} • {analysis.policy_type || 'HO-3'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-4 py-2 rounded-lg font-semibold ${getConfidenceColor(analysis.coverage_confidence)}`}>
            {Math.round((analysis.coverage_confidence || 0) * 100)}% Coverage Confidence
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadAnalysis}
            data-testid="download-policy-analysis"
          >
            <Download className="w-4 h-4 mr-1" /> Download
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={analyzePolicy}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Re-analyze'}
          </Button>
        </div>
      </div>

      {/* Coverage Summary */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50"
          onClick={() => toggleSection('coverage')}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="w-5 h-5 text-green-600" />
              Coverage Details
            </CardTitle>
            {expandedSections.coverage ? <ChevronUp /> : <ChevronDown />}
          </div>
        </CardHeader>
        {expandedSections.coverage && (
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {analysis.coverage?.dwelling_coverage && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Dwelling Coverage</p>
                  <p className="text-xl font-bold text-slate-900">
                    {formatCurrency(analysis.coverage.dwelling_coverage)}
                  </p>
                </div>
              )}
              {analysis.deductible && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Deductible</p>
                  <p className="text-xl font-bold text-slate-900">
                    {analysis.deductible.type === 'percentage' 
                      ? `${analysis.deductible.percentage}%`
                      : formatCurrency(analysis.deductible.amount)
                    }
                  </p>
                  {analysis.deductible.special_deductibles?.length > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Special deductibles apply
                    </p>
                  )}
                </div>
              )}
              {analysis.coverage?.personal_property_coverage && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Personal Property</p>
                  <p className="text-xl font-bold text-slate-900">
                    {formatCurrency(analysis.coverage.personal_property_coverage)}
                  </p>
                </div>
              )}
            </div>

            {/* Special Deductibles Alert */}
            {analysis.deductible?.special_deductibles?.length > 0 && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4" />
                  Special Deductibles
                </h4>
                <ul className="space-y-1 text-sm text-amber-700">
                  {analysis.deductible.special_deductibles.map((ded, i) => (
                    <li key={i}>
                      <strong>{ded.type}:</strong> {ded.percentage}% of {ded.applies_to}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Exclusions & Risk Factors */}
      {analysis.exclusions?.length > 0 && (
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50"
            onClick={() => toggleSection('exclusions')}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Exclusions & Limitations ({analysis.exclusions.length})
              </CardTitle>
              {expandedSections.exclusions ? <ChevronUp /> : <ChevronDown />}
            </div>
          </CardHeader>
          {expandedSections.exclusions && (
            <CardContent>
              <div className="space-y-3">
                {analysis.exclusions.map((exclusion, i) => (
                  <div 
                    key={i} 
                    className={`p-4 rounded-lg border ${getRiskColor(exclusion.risk_level)}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-semibold capitalize">{exclusion.category}</span>
                      <Badge variant="outline" className={getRiskColor(exclusion.risk_level)}>
                        {exclusion.risk_level} risk
                      </Badge>
                    </div>
                    <p className="text-sm mb-2">{exclusion.description}</p>
                    {exclusion.mitigation_strategy && (
                      <div className="mt-2 pt-2 border-t border-current border-opacity-20">
                        <p className="text-xs font-medium">Mitigation Strategy:</p>
                        <p className="text-sm">{exclusion.mitigation_strategy}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Defense Strategy */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader 
          className="cursor-pointer hover:bg-green-100"
          onClick={() => toggleSection('strategy')}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base text-green-800">
              <Scale className="w-5 h-5" />
              Claim Defense Strategy
            </CardTitle>
            {expandedSections.strategy ? <ChevronUp /> : <ChevronDown />}
          </div>
        </CardHeader>
        {expandedSections.strategy && analysis.defense_strategy && (
          <CardContent className="bg-white rounded-b-lg">
            <div className="space-y-4">
              {/* Primary Approach */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Primary Approach</h4>
                <p className="text-slate-700">{analysis.defense_strategy.primary_approach}</p>
              </div>

              {/* Key Arguments */}
              {analysis.defense_strategy.key_arguments?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Key Arguments</h4>
                  <ul className="space-y-2">
                    {analysis.defense_strategy.key_arguments.map((arg, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{arg}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Evidence to Gather */}
              {analysis.defense_strategy.evidence_to_gather?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Evidence to Gather</h4>
                  <ul className="space-y-1">
                    {analysis.defense_strategy.evidence_to_gather.map((evidence, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                        {evidence}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Counter Arguments */}
              {analysis.defense_strategy.counter_arguments?.length > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">If They Object...</h4>
                  <ul className="space-y-2">
                    {analysis.defense_strategy.counter_arguments.map((counter, i) => (
                      <li key={i} className="text-blue-800 text-sm">
                        → {counter}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Adjuster Talking Points */}
      {analysis.adjuster_talking_points?.length > 0 && (
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50"
            onClick={() => toggleSection('talkingPoints')}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                Adjuster Talking Points ({analysis.adjuster_talking_points.length})
              </CardTitle>
              {expandedSections.talkingPoints ? <ChevronUp /> : <ChevronDown />}
            </div>
          </CardHeader>
          {expandedSections.talkingPoints && (
            <CardContent>
              <div className="space-y-3">
                {analysis.adjuster_talking_points.map((point, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                      {i + 1}
                    </span>
                    <p className="text-slate-700">{point}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Policy Citations */}
      {analysis.policy_citations?.length > 0 && (
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50"
            onClick={() => toggleSection('citations')}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <BookOpen className="w-5 h-5 text-purple-600" />
                Policy Citations to Reference ({analysis.policy_citations.length})
              </CardTitle>
              {expandedSections.citations ? <ChevronUp /> : <ChevronDown />}
            </div>
          </CardHeader>
          {expandedSections.citations && (
            <CardContent>
              <div className="space-y-4">
                {analysis.policy_citations.map((citation, i) => (
                  <div key={i} className="border-l-4 border-purple-500 pl-4">
                    <p className="font-semibold text-purple-900">{citation.section}</p>
                    <blockquote className="text-sm text-slate-600 italic my-1">
                      "{citation.quote}"
                    </blockquote>
                    <p className="text-xs text-slate-500">{citation.relevance}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Recommended Actions */}
      {analysis.recommended_actions?.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="w-5 h-5 text-orange-600" />
              Recommended Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {analysis.recommended_actions.map((action, i) => (
                <li key={i} className="flex items-center gap-3">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-slate-700">{action}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Risk Factors */}
      {analysis.risk_factors?.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-red-800">
              <XCircle className="w-5 h-5" />
              Risk Factors to Watch
            </CardTitle>
          </CardHeader>
          <CardContent className="bg-white rounded-b-lg">
            <ul className="space-y-2">
              {analysis.risk_factors.map((risk, i) => (
                <li key={i} className="flex items-start gap-2 text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{risk}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PolicyAnalysisTab;
